import { DataConnectorInput } from '@automata/wfb-api';
import type { DataConnectorType } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import mapKeys from 'lodash/mapKeys';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';

import type { DataExportTaskProperties } from '../../../types';
import { DataDependenciesByTaskId, InputValue } from '../../shared/types';
import addDefaultArgumentValues from '../addDefaultArgumentValues';
import {
  API_DATA_EXPORT_INPUT_ID,
  LOCAL_DATA_EXPORT_INPUT_ID,
} from '../constants';

type GetExportDataTaskProps = {
  taskId: string;
  taskProperties: DataExportTaskProperties;
  taskDependencies: string[];
  dataDependenciesByTaskId: DataDependenciesByTaskId;
};

export function getExportDataTask({
  taskId,
  taskProperties,
  taskDependencies,
  dataDependenciesByTaskId,
}: GetExportDataTaskProps): DataConnectorInput {
  const dataDependencies = dataDependenciesByTaskId[taskId];

  const dependencies = uniq([
    ...taskDependencies,
    ...Object.values(dataDependencies ?? {})
      .filter((x): x is { task_id: string } => 'task_id' in x)
      .map((x) => x.task_id),
  ]);

  const taskParametersWithDefaultValues = addDefaultArgumentValues(
    taskProperties.taskType,
    taskProperties.taskParameters ?? null,
    taskProperties.taskAppliedParameters
  );

  const taskParametersInputs: DataConnectorInput['inputs'] = reduce(
    taskParametersWithDefaultValues,
    (acc, parameterValue, key) => {
      const parameterId = taskProperties.taskAppliedParameters?.[key];
      let input: InputValue | null = null;

      if (parameterId) {
        input = { parameter_id: parameterId };
      } else if (parameterValue) {
        input = { value: parameterValue };
      }

      return input ? { ...acc, [key]: input } : acc;
    },
    {}
  );

  // currently the WFB API expects 'input_task' rather than 'data_key'
  // this is due to the fact that the WFB API has a hardcoded value that it expects for the data export task
  const dependenciesInputs = mapKeys(
    dataDependenciesByTaskId[taskId],
    (_, key) =>
      key === LOCAL_DATA_EXPORT_INPUT_ID ? API_DATA_EXPORT_INPUT_ID : key
  );

  return {
    action: 'export',
    dependencies,
    description: taskProperties.nickname,
    data_connector_type: taskProperties.connectorType as DataConnectorType,
    id: taskId,
    inputs: {
      ...dependenciesInputs,
      ...taskParametersInputs,
    },
  };
}
